import * as Yup from "yup";
import { ImageWithArtistId } from "../../types/form";

export interface PitchFormValues {
  // Basic Artist and Release Information
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
  configuration: string;

  // Identifiers
  releaseUpc: string;
  focusTrackName?: string;
  trackIsrc: string;

  // Artist Details
  featuredArtists?: string;
  featuredArtistUri?: string;
  artistHometown?: string;
  artistCurrentTown?: string;

  // Release Descriptions
  altTitles?: string;
  shortDescription: string;
  longDescription: string;
  lyrics: string;
  songDescription?: string[];

  // Musical Characteristics
  mainGenre: string[];
  songMood: string[];
  language: string[];

  // Links and Media
  spotifyUri?: string;
  linkToListen?: string;
  promoPhotos?: string;
  socialsLinks?: string;

  // Platform-Specific Details
  similarArtists?: string;
  asin?: string;
  TIDALpage?: string;
  soundCloudProfileUrl?: string;
  soundCloudContact?: string;
  distributor?: string;

  // Platform-Specific Flags and Priorities
  pinToThisIsPlaylist?: boolean;
  addAmazonVoiceIntro?: boolean;
  pitchToAudiomackTrending?: boolean;
  isPremierProgram?: boolean;

  // Subgenres for each platform
  spotifySubgenre?: string[];
  amazonSubgenre?: string[];
  deezerSubgenre?: string[];
  audiomackSubgenre?: string[];
  TIDALSubgenre?: string[];
  soundCloudSubgenre?: string[];

  // Spotify specific
  musicCulture?: string[];
  instruments?: string[];
  songStyle?: string[];

  // Amazon specific
  listenersLocation?: string[];
  songCategory?: string[];

  // Deezer specific
  priority?: string;
  deezerEditorialTeam?: string;

  // TIDAL specific
  artistCountry?: string;
  LabelDistributor?: string;

  // SoundCloud specific
  promotionTerritories?: string[];
  freshMusicFriday?: string[];
  playlistConsideration?: string;
  soundCloudReleaseStage?: string;

  // Apple specific
  appleId?: string;
  appleTrackId?: string;

  // File uploads
  voiceIntro?: File;
  introImage?: File;

  // Status
  status?: string;

  // YouTube specific
  youtubePromotionSurface?: string[];
  youtubeReleaseStage?:string;
 // youtubeSongDetails?: string[];
  youtubePromotionDate?: string;
  youtubeTerritories?: string[];
  //asset files
  images?: ImageWithArtistId[];

}

// Create a function that returns the validation schema based on selected DSPs
export const createPitchValidationSchema = (selectedDSPs: {
  spotify: boolean;
  amazon: boolean;
  deezer: boolean;
  TIDAL: boolean;
  audiomack: boolean;
  soundcloud: boolean;
  apple: boolean;
  youtube: boolean;
}) => {
  return Yup.object().shape({
    // Basic required fields (always required)
    artistName: Yup.string().required("Artist name is required"),
    releaseTitle: Yup.string().required("Release title is required"),
    releaseDate: Yup.date()
      .min(new Date(), "Release date must be in the future")
      .required("Release date is required"),
    configuration: Yup.string().required("Configuration is required"),

    // Identifiers (always required)
    releaseUpc: Yup.string()
      .matches(/^\d{12}$/, "UPC must be exactly 12 digits")
      .required("Release UPC is required"),
    trackIsrc: Yup.string()
      .matches(/^[A-Z]{2}-?[A-Z0-9]{3}-?\d{2}-?\d{5}$/, "Invalid ISRC format")
      .required("Track ISRC is required"),

    // Focus track required for EP/Album
    focusTrackName: Yup.string().when("configuration", {
      is: (val: string) => val === "EP" || val === "Album",
      then: (schema) => schema.required("Focus track is required for EP/Album"),
      otherwise: (schema) => schema.notRequired(),
    }),

    // Descriptions (always required)
    shortDescription: Yup.string()
      .max(500, "Short description must be less than 500 characters")
      .required("Short description is required"),
    longDescription: Yup.string().required("Long description is required"),
    lyrics: Yup.string(),

    // Genre (always required)
    mainGenre: Yup.array()
      .of(Yup.string())
      .min(1, "Main genre is required")
      .required("Main genre is required"),

    songMood: Yup.array()
      .of(Yup.string())
      .min(1, "Song mood is required")
      .required("Song mood is required"),

    language: Yup.array()
      .of(Yup.string())
      .min(1, "Language is required")
      .required("Language is required"),

    songDescription: Yup.array()
      .of(Yup.string())
      .min(1, "Song description is required")
      .required("Song description is required"),

    // Links and Media (always required)
    linkToListen: Yup.string().url().required("Link to listen is required"),
    promoPhotos: Yup.string().url().required("Promo photos are required"),
    images: Yup.array(),
     

    // Platform-specific subgenres (conditional)
    spotifySubgenre: selectedDSPs.spotify
      ? Yup.array().of(Yup.string()).max(3, "Maximum 3 subgenres allowed")
      : Yup.array(),
    amazonSubgenre: selectedDSPs.amazon
      ? Yup.array().of(Yup.string()).max(3, "Maximum 3 subgenres allowed")
      : Yup.array(),
    deezerSubgenre: selectedDSPs.deezer
      ? Yup.array().of(Yup.string()).max(3, "Maximum 3 subgenres allowed")
      : Yup.array(),
    audiomackSubgenre: selectedDSPs.audiomack
      ? Yup.array().of(Yup.string()).max(3, "Maximum 3 subgenres allowed")
      : Yup.array(),
    TIDALSubgenre: selectedDSPs.TIDAL
      ? Yup.array().of(Yup.string()).max(3, "Maximum 3 subgenres allowed")
      : Yup.array(),
    soundCloudSubgenre: selectedDSPs.soundcloud
      ? Yup.array().of(Yup.string()).max(3, "Maximum 3 subgenres allowed")
      : Yup.array(),

    // Spotify specific (conditional)
    spotifyUri: selectedDSPs.spotify
      ? Yup.string().required("Spotify URI is required")
      : Yup.string(),
    artistHometown: selectedDSPs.spotify
      ? Yup.string().required("Artist Hometown is Required")
      : Yup.string(),
    artistCurrentTown: selectedDSPs.spotify
      ? Yup.string().required("Artist Current Town is Required")
      : Yup.string(),
    musicCulture: selectedDSPs.spotify
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Music Culture is Required")
          .max(2, "Maximum 2 music cultures allowed")
          .required("Music Culture is Required")
      : Yup.array(),
    instruments: selectedDSPs.spotify
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Instruments is Required")
          .required("Instruments is Required")
      : Yup.array(),
    songStyle: selectedDSPs.spotify
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Song Style is Required")
          .max(3, "Maximum 3 song styles allowed")
          .required("Song Style is Required")
      : Yup.array(),

    // Amazon specific (conditional)
    listenersLocation: selectedDSPs.amazon
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Listeners Location is Required")
          .max(1, "Maximum 1 location allowed")
          .required("Listeners Location is Required")
      : Yup.array(),
    songCategory: selectedDSPs.amazon
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Song Category is Required")
          .max(3, "Maximum 3 categories allowed")
          .required("Song Category is Required")
      : Yup.array(),
    similarArtists: selectedDSPs.amazon
      ? Yup.string().required("Similar Artists is Required")
      : Yup.string(),

    // Deezer specific (conditional)
    priority: selectedDSPs.deezer
      ? Yup.string().required("Priority is Required")
      : Yup.string(),
    deezerEditorialTeam: selectedDSPs.deezer
      ? Yup.string().required("Deezer Editorial Team is Required")
      : Yup.string(),

    // TIDAL specific (conditional)
    TIDALpage: selectedDSPs.TIDAL
      ? Yup.string()
          .url("Must be a valid URL")
          .required("Tidal Page is Required")
      : Yup.string(),
    artistCountry: selectedDSPs.TIDAL
      ? Yup.string().required("Artist Country is Required")
      : Yup.string(),
    LabelDistributor: selectedDSPs.TIDAL
      ? Yup.string().required("Label Distributor is Required")
      : Yup.string(),
    socialsLinks: selectedDSPs.TIDAL
      ? Yup.string().required("Socials Links is Required")
      : Yup.string(),

    // SoundCloud specific (conditional)
    soundCloudProfileUrl: selectedDSPs.soundcloud
      ? Yup.string()
          .url("Must be a valid URL")
          .required("SoundCloud Profile URL is Required")
      : Yup.string(),
    soundCloudContact: selectedDSPs.soundcloud
      ? Yup.string().required("SoundCloud Contact is Required")
      : Yup.string(),
    distributor: selectedDSPs.soundcloud
      ? Yup.string().required("Distributor is Required")
      : Yup.string(),
    promotionTerritories: selectedDSPs.soundcloud
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Promotion Territories is Required")
          .required("Promotion Territories is Required")
      : Yup.array(),
    freshMusicFriday: selectedDSPs.soundcloud
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Fresh Music Friday is Required")
          .required("Fresh Music Friday is Required")
      : Yup.array(),
    playlistConsideration: selectedDSPs.soundcloud
      ? Yup.string().required("Playlist Consideration is Required")
      : Yup.string(),
    soundCloudReleaseStage: selectedDSPs.soundcloud
      ? Yup.string().required("Release Stage is Required")
      : Yup.string(),

    // Apple specific (conditional)
    appleId: selectedDSPs.apple ? Yup.string().required("Apple ID is required"): Yup.string(),
    appleTrackId: selectedDSPs.apple ? Yup.string().required("Apple Track ID is required"):Yup.string(),

    // Optional fields (not conditional)
    featuredArtists: Yup.string(),
    featuredArtistUri: Yup.string(),
    altTitles: Yup.string(),
    asin: Yup.string(),

    // Booleans
    pinToThisIsPlaylist: Yup.boolean(),
    addAmazonVoiceIntro: Yup.boolean(),
    pitchToAudiomackTrending: Yup.boolean(),
    isPremierProgram: Yup.boolean(),

    // Status
    status: Yup.string(),

    // YouTube specific (conditional)
    youtubePromotionSurface: selectedDSPs.youtube
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Promotion Surface is Required")
          .required("Promotion Surface is Required")
      : Yup.array(),
      youtubeReleaseStage: selectedDSPs.youtube
      ? Yup.string().required("Release Stage is Required")
      : Yup.string(),
    // youtubeSongDetails: selectedDSPs.youtube
    //   ? Yup.array()
    //       .of(Yup.string())
    //       .min(1, "Song Details is Required")
    //       .max(2, "Maximum 2 song details allowed")
    //       .required("Song Details is Required")
    //   : Yup.array(),
    youtubePromotionDate: selectedDSPs.youtube
      ? Yup.date()
          .min(new Date(), "Promotion date must be in the future")
          .required("Promotion date is required")
      : Yup.date(),
    youtubeTerritories: selectedDSPs.youtube
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Territories is Required")
          .max(3, "Maximum 3 territories allowed")
          .required("Territories is Required")
      : Yup.array(),
  });
};

// Keep the old schema for backward compatibility (but it will be deprecated)
export const pitchValidationSchema = Yup.object().shape({
  // Basic required fields
  artistName: Yup.string().required("Artist name is required"),
  releaseTitle: Yup.string().required("Release title is required"),
  releaseDate: Yup.date()
    .min(new Date(), "Release date must be in the future")
    .required("Release date is required"),
  configuration: Yup.string().required("Configuration is required"),

  // Identifiers
  releaseUpc: Yup.string()
    .matches(/^\d{12}$/, "UPC must be exactly 12 digits")
    .required("Release UPC is required"),
  trackIsrc: Yup.string()
    .matches(/^[A-Z]{2}-?[A-Z0-9]{3}-?\d{2}-?\d{5}$/, "Invalid ISRC format")
    .required("Track ISRC is required"),

  // Focus track required for EP/Album
  focusTrackName: Yup.string().when("configuration", {
    is: (val: string) => val === "EP" || val === "Album",
    then: (schema) => schema.required("Focus track is required for EP/Album"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Descriptions
  shortDescription: Yup.string()
    .max(500, "Short description must be less than 500 characters")
    .required("Short description is required"),
  longDescription: Yup.string().required("Long description is required"),
  lyrics: Yup.string(),

  // Genre
  mainGenre: Yup.array()
    .of(Yup.string())
    .min(1, "Main genre is required")
    .required("Main genre is required"),

  songMood: Yup.array()
    .of(Yup.string())
    .min(1, "Song mood is required")
    .required("Song mood is required"),

  language: Yup.array()
    .of(Yup.string())
    .min(1, "Language is required")
    .required("Language is required"),

  songDescription: Yup.array()
    .of(Yup.string())
    .min(1, "Song description is required")
    .required("Song description is required"),

  // URL validations
  linkToListen: Yup.string().url().required("Link to listen is required"),
  promoPhotos: Yup.string().url().required("Promo photos are required"),
  spotifyUri: Yup.string().required("Spotify URI is required"),
  TIDALpage: Yup.string()
    .url("Must be a valid URL")
    .required("Tidal Page is Required"),
  soundCloudProfileUrl: Yup.string()
    .url("Must be a valid URL")
    .required("SoundCloud Profile URL is Required"),

  isEmail: Yup.boolean().default(false),

  // Platform-specific subgenres
  spotifySubgenre: Yup.array()
    .of(Yup.string())
    .max(3, "Maximum 3 subgenres allowed"),
  amazonSubgenre: Yup.array()
    .of(Yup.string())
    .max(3, "Maximum 3 subgenres allowed"),
  deezerSubgenre: Yup.array()
    .of(Yup.string())
    .max(3, "Maximum 3 subgenres allowed"),
  audiomackSubgenre: Yup.array()
    .of(Yup.string())
    .max(3, "Maximum 3 subgenres allowed"),
  TIDALSubgenre: Yup.array()
    .of(Yup.string())
    .max(3, "Maximum 3 subgenres allowed"),
  soundCloudSubgenre: Yup.array()
    .of(Yup.string())
    .max(3, "Maximum 3 subgenres allowed"),

  // Spotify specific
  musicCulture: Yup.array()
    .of(Yup.string())
    .min(1, "Music Culture is Required")
    .max(2, "Maximum 2 music cultures allowed")
    .required("Music Culture is Required"),
  instruments: Yup.array()
    .of(Yup.string())
    .min(1, "Instruments is Required")
    .required("Instruments is Required"),
  songStyle: Yup.array()
    .of(Yup.string())
    .min(1, "Song Style is Required")
    .max(3, "Maximum 3 song styles allowed")
    .required("Song Style is Required"),

  // Amazon specific
  listenersLocation: Yup.array()
    .of(Yup.string())
    .min(1, "Listeners Location is Required")
    .max(1, "Maximum 1 location allowed")
    .required("Listeners Location is Required"),
  songCategory: Yup.array()
    .of(Yup.string())
    .min(1, "Song Category is Required")
    .max(3, "Maximum 3 categories allowed")
    .required("Song Category is Required"),

  // SoundCloud specific
  promotionTerritories: Yup.array()
    .of(Yup.string())
    .min(1, "Promotion Territories is Required")
    .required("Promotion Territories is Required"),
  freshMusicFriday: Yup.array()
    .of(Yup.string())
    .min(1, "Fresh Music Friday is Required")
    .required("Fresh Music Friday is Required"),
  soundCloudContact: Yup.string().required("SoundCloud Contact is Required"),

  // Apple specific
  appleId: Yup.string(),
  appleTrackId: Yup.string(),

  // Optional strings
  featuredArtists: Yup.string(),
  featuredArtistUri: Yup.string(),
  artistHometown: Yup.string().required("Artist Hometown is Required"),
  artistCurrentTown: Yup.string().required("Artist Current Town is Required"),
  altTitles: Yup.string(),
  socialsLinks: Yup.string().required("Socials Links is Required"),
  similarArtists: Yup.string().required("Similar Artists is Required"),
  asin: Yup.string(),
  distributor: Yup.string().required("Distributor is Required"),
  priority: Yup.string().required("Priority is Required"),
  deezerEditorialTeam: Yup.string().required(
    "Deezer Editorial Team is Required"
  ),
  artistCountry: Yup.string().required("Artist Country is Required"),
  LabelDistributor: Yup.string().required("Label Distributor is Required"),
  playlistConsideration: Yup.string().required(
    "Playlist Consideration is Required"
  ),

  // Booleans
  pinToThisIsPlaylist: Yup.boolean(),
  addAmazonVoiceIntro: Yup.boolean(),
  pitchToAudiomackTrending: Yup.boolean(),
  isPremierProgram: Yup.boolean(),

  // Status
  status: Yup.string(),
});
